/**
 * A small QR code encoder: byte mode, error correction level M, versions 1 to 40, the
 * standard mask selection, and SVG output. Ours, with no runtime dependency (Andrew,
 * 2026-10-01: build our own, validate once against an established library). The check
 * lives in qr.test.ts, which compares every module against the `qrcode` package (a
 * devDependency only).
 *
 * Follows ISO/IEC 18004: data codewords, Reed-Solomon error correction over GF(256)
 * with the 0x11D polynomial, block interleaving, function patterns, the zig-zag
 * placement, the eight masks scored by the four penalty rules, then format and version
 * information.
 */

/** A QR symbol: `modules[y][x]` is true for a dark module. */
export interface QrCode {
  version: number;
  size: number;
  mask: number;
  modules: boolean[][];
}

// Error correction level M, indexed by version (index 0 unused).
const ECC_PER_BLOCK_M = [
  -1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26,
  26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
];
const BLOCKS_M = [
  -1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16,
  17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49,
];
/** The two format bits for level M. */
const ECL_BITS_M = 0;

/** Modules available for data and error correction in a version. */
function rawDataModules(ver: number): number {
  let n = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const align = Math.floor(ver / 7) + 2;
    n -= (25 * align - 10) * align - 55;
    if (ver >= 7) n -= 36;
  }
  return n;
}

function dataCodewords(ver: number): number {
  return Math.floor(rawDataModules(ver) / 8) - ECC_PER_BLOCK_M[ver] * BLOCKS_M[ver];
}

// ── GF(256) and Reed-Solomon ──────────────────────────────────────────────────

function gfMul(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

function rsDivisor(degree: number): number[] {
  const out = new Array<number>(degree).fill(0);
  out[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < out.length; j++) {
      out[j] = gfMul(out[j], root);
      if (j + 1 < out.length) out[j] ^= out[j + 1];
    }
    root = gfMul(root, 0x02);
  }
  return out;
}

function rsRemainder(data: number[], divisor: number[]): number[] {
  const out = new Array<number>(divisor.length).fill(0);
  for (const b of data) {
    const factor = b ^ (out.shift() as number);
    out.push(0);
    divisor.forEach((coef, i) => { out[i] ^= gfMul(coef, factor); });
  }
  return out;
}

// ── Data ──────────────────────────────────────────────────────────────────────

function utf8(text: string): number[] {
  return [...new TextEncoder().encode(text)];
}

/** The smallest version (1..40) that holds `n` bytes in byte mode at level M. */
function versionFor(n: number): number {
  for (let ver = 1; ver <= 40; ver++) {
    const countBits = ver <= 9 ? 8 : 16;
    if (4 + countBits + n * 8 <= dataCodewords(ver) * 8) return ver;
  }
  throw new RangeError('Too much data for a QR code');
}

function dataBits(bytes: number[], ver: number): number[] {
  const bits: number[] = [];
  const put = (val: number, len: number) => { for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1); };
  put(0b0100, 4);
  put(bytes.length, ver <= 9 ? 8 : 16);
  for (const b of bytes) put(b, 8);
  const cap = dataCodewords(ver) * 8;
  put(0, Math.min(4, cap - bits.length));
  put(0, (8 - (bits.length % 8)) % 8);
  for (let pad = 0xec; bits.length < cap; pad ^= 0xec ^ 0x11) put(pad, 8);
  return bits;
}

function codewords(bytes: number[], ver: number): number[] {
  const bits = dataBits(bytes, ver);
  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) data.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));
  const numBlocks = BLOCKS_M[ver];
  const eccLen = ECC_PER_BLOCK_M[ver];
  const raw = Math.floor(rawDataModules(ver) / 8);
  const numShort = numBlocks - (raw % numBlocks);
  const shortLen = Math.floor(raw / numBlocks);
  const div = rsDivisor(eccLen);
  const blocks: number[][] = [];
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const dat = data.slice(k, k + shortLen - eccLen + (i < numShort ? 0 : 1));
    k += dat.length;
    const ecc = rsRemainder(dat, div);
    if (i < numShort) dat.push(0);
    blocks.push(dat.concat(ecc));
  }
  const out: number[] = [];
  for (let i = 0; i < blocks[0].length; i++) {
    blocks.forEach((block, j) => {
      if (i !== shortLen - eccLen || j >= numShort) out.push(block[i]);
    });
  }
  return out;
}

// ── The matrix ────────────────────────────────────────────────────────────────

function alignmentPositions(ver: number): number[] {
  if (ver === 1) return [];
  const n = Math.floor(ver / 7) + 2;
  const step = ver === 32 ? 26 : Math.ceil((ver * 4 + 4) / (n * 2 - 2)) * 2;
  const out = [6];
  for (let pos = ver * 4 + 17 - 7; out.length < n; pos -= step) out.splice(1, 0, pos);
  return out;
}

class Grid {
  readonly size: number;
  readonly m: boolean[][];
  readonly fn: boolean[][];
  constructor(readonly ver: number) {
    this.size = ver * 4 + 17;
    this.m = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
    this.fn = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
  }
  setFn(x: number, y: number, dark: boolean): void {
    this.m[y][x] = dark;
    this.fn[y][x] = true;
  }
  functionPatterns(): void {
    const s = this.size;
    for (let i = 0; i < s; i++) {
      this.setFn(6, i, i % 2 === 0);
      this.setFn(i, 6, i % 2 === 0);
    }
    this.finder(3, 3);
    this.finder(s - 4, 3);
    this.finder(3, s - 4);
    const pos = alignmentPositions(this.ver);
    const n = pos.length;
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if ((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0)) continue;
        for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) this.setFn(pos[i] + dx, pos[j] + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
    this.format(0);
    this.version();
  }
  finder(x: number, y: number): void {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        const xx = x + dx;
        const yy = y + dy;
        if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) this.setFn(xx, yy, d !== 2 && d !== 4);
      }
    }
  }
  format(mask: number): void {
    const data = (ECL_BITS_M << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = ((data << 10) | rem) ^ 0x5412;
    const bit = (i: number) => ((bits >>> i) & 1) !== 0;
    const s = this.size;
    for (let i = 0; i <= 5; i++) this.setFn(8, i, bit(i));
    this.setFn(8, 7, bit(6));
    this.setFn(8, 8, bit(7));
    this.setFn(7, 8, bit(8));
    for (let i = 9; i < 15; i++) this.setFn(14 - i, 8, bit(i));
    for (let i = 0; i < 8; i++) this.setFn(s - 1 - i, 8, bit(i));
    for (let i = 8; i < 15; i++) this.setFn(8, s - 15 + i, bit(i));
    this.setFn(8, s - 8, true);
  }
  version(): void {
    if (this.ver < 7) return;
    let rem = this.ver;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (this.ver << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) !== 0;
      const a = this.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.setFn(a, b, dark);
      this.setFn(b, a, dark);
    }
  }
  place(data: number[]): void {
    let i = 0;
    const s = this.size;
    for (let right = s - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < s; vert++) {
        for (let j = 0; j < 2; j++) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? s - 1 - vert : vert;
          if (!this.fn[y][x] && i < data.length * 8) {
            this.m[y][x] = ((data[i >>> 3] >>> (7 - (i & 7))) & 1) !== 0;
            i++;
          }
        }
      }
    }
  }
  applyMask(mask: number): void {
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        if (this.fn[y][x]) continue;
        let flip: boolean;
        switch (mask) {
          case 0: flip = (x + y) % 2 === 0; break;
          case 1: flip = y % 2 === 0; break;
          case 2: flip = x % 3 === 0; break;
          case 3: flip = (x + y) % 3 === 0; break;
          case 4: flip = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
          case 5: flip = ((x * y) % 2) + ((x * y) % 3) === 0; break;
          case 6: flip = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          default: flip = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
        }
        if (flip) this.m[y][x] = !this.m[y][x];
      }
    }
  }
  /** The four penalty rules (ISO/IEC 18004 7.8.3): runs, 2x2 blocks, finder-like
   *  1:1:3:1:1 runs with four light modules beside them, and dark/light balance. */
  penalty(): number {
    const s = this.size;
    const at = (r: number, c: number) => (this.m[r][c] ? 1 : 0);
    let n1 = 0;
    for (let r = 0; r < s; r++) {
      let runRow = 0, lastRow = -1, runCol = 0, lastCol = -1;
      for (let c = 0; c < s; c++) {
        const a = at(r, c);
        if (a === lastRow) runRow++; else { if (runRow >= 5) n1 += 3 + (runRow - 5); lastRow = a; runRow = 1; }
        const b = at(c, r);
        if (b === lastCol) runCol++; else { if (runCol >= 5) n1 += 3 + (runCol - 5); lastCol = b; runCol = 1; }
      }
      if (runRow >= 5) n1 += 3 + (runRow - 5);
      if (runCol >= 5) n1 += 3 + (runCol - 5);
    }
    let n2 = 0;
    for (let r = 0; r < s - 1; r++) {
      for (let c = 0; c < s - 1; c++) {
        const sum = at(r, c) + at(r, c + 1) + at(r + 1, c) + at(r + 1, c + 1);
        if (sum === 4 || sum === 0) n2++;
      }
    }
    let n3 = 0;
    for (let r = 0; r < s; r++) {
      let bitsRow = 0, bitsCol = 0;
      for (let c = 0; c < s; c++) {
        bitsRow = ((bitsRow << 1) & 0x7ff) | at(r, c);
        if (c >= 10 && (bitsRow === 0x5d0 || bitsRow === 0x05d)) n3++;
        bitsCol = ((bitsCol << 1) & 0x7ff) | at(c, r);
        if (c >= 10 && (bitsCol === 0x5d0 || bitsCol === 0x05d)) n3++;
      }
    }
    let dark = 0;
    for (let r = 0; r < s; r++) for (let c = 0; c < s; c++) dark += at(r, c);
    const k = Math.abs(Math.ceil((dark * 100) / (s * s) / 5) - 10);
    return n1 + n2 * 3 + n3 * 40 + k * 10;
  }
}

/**
 * Encode `text` (as UTF-8 bytes, byte mode, level M). `mask` forces one of the eight
 * masks; by default the one with the lowest penalty wins (the first on a tie).
 */
export function encodeQr(text: string, opts: { mask?: number } = {}): QrCode {
  const bytes = utf8(text);
  const ver = versionFor(bytes.length);
  const data = codewords(bytes, ver);
  const build = (mask: number): Grid => {
    const g = new Grid(ver);
    g.functionPatterns();
    g.place(data);
    g.format(mask);
    g.applyMask(mask);
    return g;
  };
  let mask = opts.mask ?? -1;
  if (mask < 0 || mask > 7) {
    let best = Infinity;
    for (let k = 0; k < 8; k++) {
      const p = build(k).penalty();
      if (p < best) { best = p; mask = k; }
    }
  }
  const g = build(mask);
  return { version: ver, size: g.size, mask, modules: g.m };
}

/**
 * The symbol as an SVG string: one path of dark modules on a light ground, with the
 * standard four-module quiet zone. Scales to whatever box holds it.
 */
export function qrSvg(text: string, opts: { dark?: string; light?: string; title?: string } = {}): string {
  const qr = encodeQr(text);
  const quiet = 4;
  const dim = qr.size + quiet * 2;
  let d = '';
  for (let y = 0; y < qr.size; y++) {
    for (let x = 0; x < qr.size; x++) {
      if (qr.modules[y][x]) d += `M${x + quiet} ${y + quiet}h1v1h-1z`;
    }
  }
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const title = opts.title ? `<title>${esc(opts.title)}</title>` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" shape-rendering="crispEdges" role="img">${title}`
    + `<rect width="${dim}" height="${dim}" fill="${opts.light ?? '#FFFFFF'}"/><path d="${d}" fill="${opts.dark ?? '#22272E'}"/></svg>`;
}
